import { useCallback, useEffect, useState } from 'react';
import { Form, Input, Button, DatePicker } from 'antd';
import FormSection from 'components/ui/form-section';
import {
  CreateBrokerPayloadDtoStatusEnum,
  CreateSubmissionPayloadDtoStatusEnum,
  CreateSubmissionPayloadDtoPolicyTypeEnum,
  CreateSubmissionPayloadDto,
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  InsuredResponseDtoStatusEnum,
  SortOptionDtoOrderEnum,
} from 'libs/pinnacle-sdk';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { isEmpty, isEqual, omit, uniqBy } from 'lodash';
import { sanitizeFormValue, validateForm } from 'utils/form';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';
import { EFormSectionStatus } from 'components/ui/form-section/types';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import CalendarIcon from 'assets/icons/calendar';
import {
  initialValues,
  getValidationSchema,
  EFormStage,
  getStageFields,
  fields,
} from './form';
import { useLocation, useNavigate } from 'react-router';
import { ENavigationPath } from 'constants/path';
import FormContainer from 'components/ui/form-container';
import { insuredApi, referenceDataApi, userApi } from 'services/apis';
import PlusIcon from 'assets/icons/plus';
import FormLayoutGroup from 'components/ui/form-layout-group';
import { useTranslation } from 'react-i18next';
import { SubmissionResponseDtoTransactionTypeEnum } from 'libs/pinnacle-sdk';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import CustomBadge from 'components/ui/custom-badge';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import TextArea from 'antd/es/input/TextArea';
import * as yup from 'yup';
import { SubmissionReview } from 'components/features/submission-review';
import { CustomFile } from 'components/features/file-uploader/types';
import { v4 as uuidv4 } from 'uuid';
import FileUploader from 'components/features/file-uploader';
import { EFileStatus } from 'components/features/file-uploader/enum';
import { useSubmissionFormStore } from 'stores/submission-form';
import { CREATE_NEW_INSURED_SELECTION_KEY } from './types';
import { useAuth } from 'hooks/useAuth';
import { SortedSelect } from 'components/ui/sorted-select';
import { ECountries } from 'constants/enums';

export default function CreateSubmissionForm() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [form] = Form.useForm<CreateSubmissionPayloadDto>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    form.setFieldValue('assignedHandlerId', user?.userId);
  }, [user?.userId]);

  const { formData, setFormData, resetForm } = useSubmissionFormStore();

  const [currentStage, setCurrentStage] = useState<EFormStage>(
    EFormStage.TRANSACTION_DETAILS
  );
  const [isReviewSubmission, setIsReviewSubmission] = useState(false);
  const [submission, setSubmission] = useState<CreateSubmissionPayloadDto>();
  const [fileList, setFileList] = useState<CustomFile[]>([]);
  const [fileSessionId, setFileSessionId] = useState('');

  const policyType = Form.useWatch('policyType', form);
  const placingType = Form.useWatch('placingType', form);
  const lineOfBusiness = Form.useWatch('lineOfBusiness', form);

  useEffect(() => {
    const newId = uuidv4();
    setFileSessionId(newId);
  }, []);

  const resetAndNavigate = useCallback(() => {
    form.resetFields();
    setShowConfirmation(false);
    resetForm();
    navigate(ENavigationPath.DASHBOARD);
  }, [form, navigate, resetForm]);

  const validateFormFields = useCallback(async () => {
    const values = form.getFieldsValue();
    const isValid = await validateForm(
      values,
      form,
      fields,
      getValidationSchema(t)
    );
    setIsFormValid(isValid);
  }, [form, t]);

  const cancelSubmission = useCallback(() => {
    const currentValue = form.getFieldsValue();
    if (isEqual(currentValue, initialValues)) return resetAndNavigate();

    return setShowConfirmation(true);
  }, [form, resetAndNavigate]);

  const validateStage = useCallback(
    async (stage: EFormStage) => {
      const status = form.getFieldValue('status');
      const fields = getStageFields(stage, status);
      if (isEmpty(fields)) return true;

      const stageValues = form.getFieldsValue(
        fields as (keyof CreateSubmissionPayloadDto)[]
      );

      // Get validation schema
      const schema = getValidationSchema(t);
      const stageSchema = Object.keys(stageValues).reduce(
        (acc, field) => ({
          ...acc,
          [field]: schema.fields[field as keyof typeof schema.fields],
        }),
        {}
      );

      try {
        const isValid = await yup
          .object(stageSchema)
          .validate(stageValues, { abortEarly: false });

        return !!isValid;
      } catch {
        return false;
      }
    },
    [form, t]
  );

  useEffect(() => {
    if (location.state?.from === ENavigationPath.NEW_INSURED) {
      form.setFieldsValue(formData);

      navigate(location.pathname, { replace: true });

      // Validate and set correct stage
      const validateStages = async () => {
        const stages = Object.values(EFormStage);

        let lastValidStage = EFormStage.SUBMISSION_INFO;

        for (const stage of stages) {
          const isValid = await validateStage(stage);
          if (!isValid) break;
          lastValidStage = stage;
        }
        const currentIndex = stages.indexOf(lastValidStage);
        const nextStage = stages[currentIndex + 1] || lastValidStage;

        setCurrentStage(nextStage);
        validateFormFields();
      };

      validateStages();
    }
  }, [
    location.state,
    formData,
    form,
    location.pathname,
    navigate,
    validateFormFields,
    validateStage,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const currentValues = form.getFieldsValue();
      if (!isEqual(currentValues, initialValues)) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [form]);

  const getSectionStatus = useCallback(
    (stage: EFormStage) => {
      if (stage === currentStage) {
        return EFormSectionStatus.FILLING;
      }

      const stages = Object.values(EFormStage);
      const currentIndex = stages.indexOf(currentStage);
      const stageIndex = stages.indexOf(stage);

      // For previous stages, check completion
      if (stageIndex < currentIndex) {
        const status = form.getFieldValue('status');
        const stageFields = getStageFields(stage, status);
        const values = form.getFieldsValue();

        const isComplete = stageFields.every((field) => {
          const value = values[field];
          return value !== undefined && value !== null && value !== '';
        });

        return isComplete
          ? EFormSectionStatus.FILLED
          : EFormSectionStatus.FILLING;
      }

      return EFormSectionStatus.TO_BE_FILLED;
    },
    [form, currentStage]
  );

  const { data: placingTypes, isLoading: isLoadingPlacingTypes } = useQuery(
    [EQueryKeys.PLACING_TYPES, policyType],
    async () => {
      return referenceDataApi.getPlacingTypes({ policyType });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery(
    EQueryKeys.CURRENCIES,
    async () => {
      return referenceDataApi.getCurrencies();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const {
    data: mainRegionAndRiskTerritoryList,
    isLoading: isLoadingMainRegionAndRiskTerritoryList,
  } = useQuery(
    EQueryKeys.MAIN_REGION_AND_RISK_TERRITORY_LIST,
    async () => {
      return referenceDataApi.getMainRegionAndRiskTerritoryList({
        getMainRegionAndRiskTerritoryListQueryDto: {},
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: subClasses, isLoading: isLoadingSubClasses } = useQuery(
    [EQueryKeys.SUB_CLASSES, lineOfBusiness],
    async () => {
      return referenceDataApi.getSubClasses({ lineOfBusiness });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: businessLines, isLoading: isLoadingBusinessLines } = useQuery(
    [EQueryKeys.BUSINESS_LINES],
    async () => {
      return referenceDataApi.getBusinessLines();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const {
    data: principalOccupations,
    isLoading: isLoadingPrincipalOccupations,
  } = useQuery(
    EQueryKeys.PRINCIPAL_OCCUPATIONS,
    async () => {
      return referenceDataApi.getPrincipalOccupations();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  return (
    <FormContainer title={t('submission.newSubmissionTitle')}>
      {isReviewSubmission && submission ? (
        <SubmissionReview
          submission={submission}
          onCancel={cancelSubmission}
          onEdit={() => setIsReviewSubmission(false)}
          fileList={fileList}
          sessionId={fileSessionId}
          entityType={CreateUploadedFilePayloadDtoEntityTypeEnum.Submission}
        />
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={async (submission: CreateSubmissionPayloadDto) => {
            const sanitizedSubmission = sanitizeFormValue(submission);
            setSubmission(sanitizedSubmission);
            setIsReviewSubmission(true);
          }}
          className="space-y-8"
          initialValues={initialValues}
          onValuesChange={async (changedValues) => {
            if ('policyType' in changedValues) {
              form.setFieldsValue({
                lineOfBusiness: undefined,
                subClass: undefined,
                placingType: undefined,
              });
            }

            if ('placingType' in changedValues) {
              form.setFieldsValue({
                lineOfBusiness: undefined,
                subClass: undefined,
              });
            }

            if ('lineOfBusiness' in changedValues) {
              form.setFieldValue('subClass', undefined);
            }

            validateFormFields();

            // Check if current stage is complete
            const isStageValid = await validateStage(currentStage);
            if (isStageValid) {
              // Move to next stage
              switch (currentStage) {
                case EFormStage.SUBMISSION_INFO:
                  setCurrentStage(EFormStage.TRANSACTION_DETAILS);
                  break;
                case EFormStage.TRANSACTION_DETAILS:
                  setCurrentStage(EFormStage.REGISTRATION);
                  break;
                case EFormStage.REGISTRATION:
                  setCurrentStage(EFormStage.UPLOAD);
                  break;
              }
            }
          }}
        >
          <FormSection
            title={t('submission.submissionInformation')}
            status={getSectionStatus(EFormStage.SUBMISSION_INFO)}
            collapsible
          >
            <FormLayoutGroup>
              <Form.Item name="status" label={t('submission.status')}>
                <SortedSelect
                  allowClear
                  size="large"
                  placeholder={t('submission.selectStatus')}
                  options={[
                    CreateSubmissionPayloadDtoStatusEnum.Pending,
                    CreateSubmissionPayloadDtoStatusEnum.Prospect,
                  ].map((status) => ({
                    label: status,
                    value: status,
                  }))}
                  optionRender={(option) => (
                    <CustomBadge
                      label={option.value as CreateBrokerPayloadDtoStatusEnum}
                      backgroundColor="bg-[#DBF6FE]"
                      textColor="text-[#3480D1]"
                    />
                  )}
                  labelRender={(props) => (
                    <CustomBadge
                      /* eslint-disable-next-line react/prop-types */
                      label={props.value as CreateBrokerPayloadDtoStatusEnum}
                      backgroundColor="bg-[#DBF6FE]"
                      textColor="text-[#3480D1]"
                    />
                  )}
                  suffixIcon={<ArrowDownIcon />}
                ></SortedSelect>
              </Form.Item>

              <Form.Item name="policyType" label={t('submission.policyType')}>
                <SortedSelect
                  allowClear
                  size="large"
                  placeholder={t('submission.selectPolicyType')}
                  onChange={(value) => {
                    if (
                      value ===
                        CreateSubmissionPayloadDtoPolicyTypeEnum.Insurance &&
                      form.getFieldValue('mainRegionAndRiskTerritory') ===
                        ECountries.AUSTRALIA
                    ) {
                      form.setFieldValue('mainRegionAndRiskTerritory', null);
                    }
                  }}
                  options={Object.values(
                    CreateSubmissionPayloadDtoPolicyTypeEnum
                  ).map((status) => ({
                    label: status,
                    value: status,
                  }))}
                  optionRender={(option) => (
                    <CustomBadge
                      label={
                        option.value as CreateSubmissionPayloadDtoPolicyTypeEnum
                      }
                      backgroundColor="bg-[#DBF6FE]"
                      textColor="text-[#3480D1]"
                    />
                  )}
                  labelRender={(props) => (
                    <CustomBadge
                      label={
                        /* eslint-disable-next-line react/prop-types */
                        props.value as CreateSubmissionPayloadDtoPolicyTypeEnum
                      }
                      backgroundColor="bg-[#DBF6FE]"
                      textColor="text-[#3480D1]"
                    />
                  )}
                  suffixIcon={<ArrowDownIcon />}
                ></SortedSelect>
              </Form.Item>
            </FormLayoutGroup>
          </FormSection>

          <FormSection
            title={t('submission.transactionDetailsAndClassification')}
            status={getSectionStatus(EFormStage.TRANSACTION_DETAILS)}
            collapsible
          >
            <FormLayoutGroup mdColumns={2}>
              <Form.Item
                name="transactionType"
                label={t('submission.transactionType')}
              >
                <SortedSelect
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  placeholder={t('submission.selectTransactionType')}
                  options={Object.values(
                    SubmissionResponseDtoTransactionTypeEnum
                  ).map((name) => ({
                    label: name,
                    value: name,
                  }))}
                  showSearch
                ></SortedSelect>
              </Form.Item>

              <Form.Item
                name="placingType"
                label={t('submission.placementType')}
              >
                <SortedSelect
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  disabled={!policyType}
                  placeholder={t('submission.selectPlacementType')}
                  loading={isLoadingPlacingTypes}
                  options={(placingTypes || []).map((placingType) => ({
                    label: placingType.name,
                    value: placingType.name,
                  }))}
                  showSearch
                ></SortedSelect>
              </Form.Item>
            </FormLayoutGroup>

            <FormLayoutGroup mdColumns={2}>
              <Form.Item
                name="lineOfBusiness"
                label={t('submission.lineOfBusiness')}
              >
                <SortedSelect
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  disabled={!placingType}
                  loading={isLoadingBusinessLines}
                  placeholder={t('submission.selectLineOfBusiness')}
                  options={(businessLines || []).map((businessLine) => ({
                    label: businessLine.name,
                    value: businessLine.name,
                  }))}
                  showSearch
                ></SortedSelect>
              </Form.Item>

              <Form.Item name="subClass" label={t('submission.subClass')}>
                <SortedSelect
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  disabled={!lineOfBusiness}
                  loading={isLoadingSubClasses}
                  placeholder={t('submission.selectSubClass')}
                  options={(subClasses || []).map((subClass) => ({
                    label: subClass.name,
                    value: subClass.name,
                  }))}
                  showSearch
                ></SortedSelect>
              </Form.Item>
            </FormLayoutGroup>
          </FormSection>

          <FormSection
            title={t('submission.submissionRegistration')}
            status={getSectionStatus(EFormStage.REGISTRATION)}
            collapsible
          >
            <FormLayoutGroup>
              <Form.Item
                name="assignedHandlerId"
                label={t('submission.assignedHandler')}
              >
                <LazyLoadingSelect
                  fetchOptions={async (page, pageSize, searchText) => {
                    const { items } = await userApi.filterUsers({
                      searchText,
                      page,
                      pageSize,
                    });
                    return {
                      data: items.map((user) => ({
                        label: user.name || '',
                        value: user.userId,
                      })),
                    };
                  }}
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  additionalOptions={
                    user
                      ? [
                          {
                            label: user?.name || '',
                            value: user?.userId,
                          },
                        ]
                      : []
                  }
                  placeholder={t('submission.selectUnderwriter')}
                  queryKey="underwriter-lazy-loading-select"
                />
              </Form.Item>

              <Form.Item
                name="namedUnderwriterId"
                label={t('submission.namedUnderwriter')}
              >
                <LazyLoadingSelect
                  fetchOptions={async (page, pageSize, searchText) => {
                    const { items } = await userApi.filterUsers({
                      searchText,
                      page,
                      pageSize,
                      isNamedUnderwriter: true,
                    });
                    return {
                      data: items.map((user) => ({
                        label: user.name || '',
                        value: user.userId,
                      })),
                    };
                  }}
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  placeholder={t('submission.selectUnderwriter')}
                  queryKey="assistant-lazy-loading-select"
                />
              </Form.Item>
            </FormLayoutGroup>

            <FormLayoutGroup mdColumns={2}>
              <Form.Item
                name="inceptionDate"
                label={t('submission.inceptionDate')}
              >
                <DatePicker
                  format={'DD/MM/YYYY'}
                  allowClear
                  size="large"
                  className="w-full"
                  suffixIcon={<CalendarIcon />}
                  onChange={(date) => {
                    if (date) {
                      const currentExpiryDate =
                        form.getFieldValue('expiryDate');
                      if (!currentExpiryDate) {
                        const expiryDate = date.clone().add(1, 'year');
                        form.setFieldValue('expiryDate', expiryDate);
                      }
                      validateFormFields();
                    }
                  }}
                />
              </Form.Item>

              <Form.Item name="expiryDate" label={t('submission.expiryDate')}>
                <DatePicker
                  format={'DD/MM/YYYY'}
                  allowClear
                  size="large"
                  className="w-full"
                  suffixIcon={<CalendarIcon />}
                  disabledDate={(current) => {
                    const inceptionDate = form.getFieldValue('inceptionDate');
                    return (
                      current &&
                      inceptionDate &&
                      current < inceptionDate.add(1, 'day')
                    );
                  }}
                />
              </Form.Item>
            </FormLayoutGroup>

            <FormLayoutGroup mdColumns={3}>
              <Form.Item name="insuredId" label={t('insured.insured')}>
                <LazyLoadingSelect
                  fetchOptions={async (page, pageSize, searchText) => {
                    const { items } = await insuredApi.filterInsureds({
                      page,
                      pageSize,
                      insuredFilterQueryDto: {
                        statuses: [InsuredResponseDtoStatusEnum.Active],
                        searchText,
                        sortOptions: [
                          {
                            field: 'name',
                            order: SortOptionDtoOrderEnum.Asc,
                          },
                        ],
                      },
                    });
                    return {
                      data: items.map((insured) => ({
                        label: insured.name,
                        value: insured.insuredId,
                      })),
                    };
                  }}
                  suffixIcon={<ArrowDownIcon />}
                  size="large"
                  placeholder={t('submission.enterInsured')}
                  allowClear
                  onSelect={(value) => {
                    if (value === CREATE_NEW_INSURED_SELECTION_KEY) {
                      const currentFormValues = form.getFieldsValue();
                      setFormData(
                        omit(currentFormValues, ['insuredId', 'insuredName'])
                      );
                      navigate(ENavigationPath.NEW_INSURED, {
                        state: {
                          from: ENavigationPath.NEW_SUBMISSION,
                        },
                      });
                    }
                  }}
                  additionalOptions={[
                    {
                      label: (
                        <div className="flex items-center gap-2 text-primary cursor-pointer text-[#1D67B6]">
                          <PlusIcon />
                          {t('submission.addNewInsured')}
                        </div>
                      ),
                      value: CREATE_NEW_INSURED_SELECTION_KEY,
                    },
                    ...(formData.insuredName && formData.insuredId
                      ? [
                          {
                            label: formData.insuredName,
                            value: formData.insuredId,
                          },
                        ]
                      : []),
                  ]}
                  queryKey="insured-lazy-loading-select"
                />
              </Form.Item>

              <Form.Item
                name="principalOccupation"
                label={t('submission.principalOccupation')}
              >
                <SortedSelect
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  placeholder={t('submission.selectPrincipalOccupation')}
                  loading={isLoadingPrincipalOccupations}
                  options={(principalOccupations || []).map(
                    (principalOccupation) => ({
                      label: principalOccupation.name,
                      value: principalOccupation.name,
                    })
                  )}
                  showSearch
                ></SortedSelect>
              </Form.Item>

              <Form.Item name="originalCurrency" label={t('general.currency')}>
                <SortedSelect
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  placeholder={t('submission.selectCurrency')}
                  loading={isLoadingCurrencies}
                  options={(currencies || []).map((currency) => ({
                    label: `${currency.name} (${currency.code})`,
                    value: currency.code,
                  }))}
                  showSearch
                ></SortedSelect>
              </Form.Item>
            </FormLayoutGroup>

            <FormLayoutGroup>
              <Form.Item
                name="mainRegionAndRiskTerritory"
                label={t('submission.mainRegionAndRiskTerritory')}
              >
                <SortedSelect
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  showSearch
                  placeholder={t('submission.selectMainRegionAndRiskTerritory')}
                  loading={isLoadingMainRegionAndRiskTerritoryList}
                  options={uniqBy(
                    (
                      (policyType ===
                      CreateSubmissionPayloadDtoPolicyTypeEnum.Insurance
                        ? mainRegionAndRiskTerritoryList?.filter(
                            (item) => item.country !== ECountries.AUSTRALIA
                          )
                        : mainRegionAndRiskTerritoryList) || []
                    ).map((mainRegionAndRiskTerritory) => ({
                      label: mainRegionAndRiskTerritory.country,
                      value: mainRegionAndRiskTerritory.country,
                    })),
                    'value'
                  )}
                ></SortedSelect>
              </Form.Item>
              <Form.Item
                name="keyRiskLocation"
                label={t('submission.keyRiskLocation')}
              >
                <Input
                  size="large"
                  placeholder={t('submission.enterKeyRiskLocation')}
                  autoComplete="off"
                />
              </Form.Item>
            </FormLayoutGroup>

            <FormLayoutGroup mdColumns={1}>
              <Form.Item name="comment" label={t('general.comments')}>
                <TextArea
                  rows={4}
                  placeholder={t('general.enter', {
                    field: t('general.comments'),
                  })}
                ></TextArea>
              </Form.Item>
            </FormLayoutGroup>
          </FormSection>

          <FormSection
            title={t('submission.uploadFile')}
            status={getSectionStatus(EFormStage.UPLOAD)}
            collapsible
          >
            <FormLayoutGroup mdColumns={1}>
              <Form.Item className="mt-6">
                {fileSessionId && (
                  <FileUploader
                    sessionId={fileSessionId}
                    entityType={
                      CreateUploadedFilePayloadDtoEntityTypeEnum.Submission
                    }
                    fileList={fileList}
                    setFileList={setFileList}
                  />
                )}
              </Form.Item>
            </FormLayoutGroup>
          </FormSection>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              className="w-full rounded-[12px] h-12 text-base font-medium"
              size="large"
              onClick={cancelSubmission}
              type="primary"
              variant="outlined"
              color="danger"
            >
              {t('submission.cancelSubmission')}
            </Button>
            <Button
              className="w-full rounded-[12px] h-12 text-base font-medium"
              size="large"
              htmlType="submit"
              type="primary"
              variant="outlined"
              color="primary"
              disabled={
                !isFormValid ||
                fileList.some((file) => file.status === EFileStatus.UPLOADING)
              }
            >
              {t('submission.createNewRecord')}
            </Button>
          </div>
        </Form>
      )}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={resetAndNavigate}
        title={t('forms.discardChangesTitle')}
        content={t('forms.discardChangesContent', {
          type: t('submission.submissionLowercase'),
        })}
        okText={t('forms.discardChangesOkText')}
        cancelText={t('forms.discardChangesCancelText')}
        type={ConfirmationModalType.DANGER}
      />
    </FormContainer>
  );
}
